import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Upload, 
  FileSpreadsheet, 
  FileText, 
  Trash2, 
  CheckCircle,
  Loader2
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import api from '@/lib/api';
import { toastSuccess, toastError } from '@/lib/toast';
// Constants
const KB = 1024;
const MB = KB * KB;
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * MB;
const BOQ_PREVIEW_LIMIT = 10;

const DOCUMENT_TYPES = [
  { value: 'specification', label: 'Technical Specification' },
  { value: 'design', label: 'Design Document' },
  { value: 'drawing', label: 'Drawing / Diagram' },
  { value: 'sow', label: 'Scope of Work' },
  { value: 'terms', label: 'Terms & Conditions' },
  { value: 'other', label: 'Other' },
];

// ===========================================================================
// TWO CLIENT-SIDE EXTENSION LISTS, EACH MIRRORING A SERVER LIST
// ===========================================================================
// VALID_BOQ_EXTENSIONS mirrors parse_boq_file's own ("xlsx", "xls", "csv",
// routes/documents.py). VALID_DOCUMENT_EXTENSIONS mirrors
// ALLOWED_UPLOAD_EXTENSIONS (routes/documents.py:34).
//
// NEITHER PAIR IS HELD TOGETHER BY ANYTHING. The BOQ pair predates this
// slice, is NOT in CLAUDE.md's mirror-pair registry, and no test asserts it
// agrees with the server - FileUploadSection.test.jsx's extension case
// tests the CLIENT's refusal, not agreement. It happens to agree today.
// The document list added here is a SECOND unregistered pair, added
// knowingly rather than silently: see the commit body for what would hold
// them together (a shared case table, the pattern
// location_backfill_parity_cases.json already establishes as ENFORCED).
//
// What IS pinned, below the registry bar but above nothing: both literals
// are anchored by uploadExtensionLists.test.js, so the JS side cannot drift
// unnoticed even though the cross-language join does not exist yet.
export const VALID_BOQ_EXTENSIONS = ['.csv', '.xlsx', '.xls'];

// Mirrors ALLOWED_UPLOAD_EXTENSIONS. DELIBERATELY THE SERVER'S LIST AND NOT
// A NARROWER ONE: refusing a type the server would have accepted is the
// worse error of the two (mutant K6). A buyer told "not accepted" about a
// .txt the backend takes has no way to discover the client is wrong, while
// a type that slips through is still caught at Continue with a clear
// message. So this list is a copy, not a policy.
export const VALID_DOCUMENT_EXTENSIONS = ['.pdf', '.docx', '.xlsx', '.csv', '.txt'];

const extensionOf = (filename) => {
  const dot = (filename || '').lastIndexOf('.');
  return dot === -1 ? '' : filename.substring(dot).toLowerCase();
};

// Helper function to format file size
const formatFileSize = (bytes) => {
  if (bytes < KB) return `${bytes} B`;
  if (bytes < MB) return `${(bytes / KB).toFixed(1)} KB`;
  return `${(bytes / MB).toFixed(1)} MB`;
};

// Helper function to validate BOQ file type
const isValidBoqFile = (filename) => VALID_BOQ_EXTENSIONS.includes(extensionOf(filename));

// The same check for a supporting document. CHECKED AT SELECTION, not at
// Continue (Ashok ruling, 2026-08-21). Held mode makes no server call, so
// without this a buyer could hold a .png, see it listed as attached, and
// learn only at Continue - after the draft exists and the serial is
// consumed. Before held mode the upload happened at selection and the type
// was refused there, so this is a regression this slice introduced and is
// repaired here rather than deferred.
const isValidDocumentFile = (filename) => VALID_DOCUMENT_EXTENSIONS.includes(extensionOf(filename));

/**
 * TWO MODES, DISCRIMINATED BY WHETHER A DOCUMENT EXISTS (Intake
 * Compression V1, Ashok ruling 2026-08-21).
 *
 *   HELD  (no procurementId)  - a new request. There is nothing to attach
 *         to yet and nothing may be persisted before Continue, so a BOQ
 *         goes to the STATELESS preview endpoint for its rows and the File
 *         itself is held in wizard state; a document is held outright with
 *         no server call at all. Continue uploads them.
 *   LIVE  (procurementId set) - a parsed procurement unlocked for edit.
 *         The document exists, so uploads persist immediately, exactly as
 *         they always have. Unchanged.
 *
 * The discriminator is data, not a flag threaded down from the step, for
 * the same reason the step's own read-only mode is: whether a file can be
 * attached is a fact about the document, and asking the caller to assert
 * it invites the two to disagree.
 */
export const FileUploadSection = ({
  procurementId,
  boqItems,
  setBoqItems,
  boqSource,
  setBoqSource,
  attachedDocuments,
  setAttachedDocuments,
  // Held-mode state. Absent for callers that only ever run live.
  heldBoqFile = null,
  setHeldBoqFile,
  heldDocuments = [],
  setHeldDocuments,
  getTempRequestId,
}) => {
  const held = !procurementId;
  // Server-side first, then held, so the list reads in the order the files
  // were chosen across a Continue that partially failed.
  const listedDocuments = [...(attachedDocuments || []), ...(heldDocuments || [])];
  const [uploadingBoq, setUploadingBoq] = useState(false);
  // Message-lifecycle fix (2026-07-30): ONE state holder for the
  // transient per-attempt outcome (success XOR error), never two
  // independent flags - a stale error can no longer coexist with a
  // later success (or vice versa). Persistent facts (boqSource-derived
  // provenance/caution) are NOT part of this state and are never
  // touched by attempt lifecycle - they only change when boqSource does.
  const [boqOutcome, setBoqOutcome] = useState(null); // { type: 'success' | 'error', message } | null
  // THE ROW CAP, STATED ALWAYS (Ashok ruling 2026-09-03, matching the
  // character-counter ruling: a limit the buyer cannot see until they hit
  // it is invisible at the point of use). Fetched from the procurement-
  // free template route, which returns agents.INTAKE_BOQ_ROW_CAP - never a
  // literal here. null until it arrives, and the standing sentence simply
  // does not render until then; it never guesses.
  const [rowCap, setRowCap] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const response = await api.getBoqTemplate();
        const cap = response?.data?.row_cap;
        if (alive && Number.isFinite(cap)) setRowCap(cap);
      } catch (err) {
        // No cap sentence rather than a wrong one; the per-parse row_cap on
        // the upload response still drives the over-cap warning.
      }
    })();
    return () => { alive = false; };
  }, []);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  // Same shape and same lifecycle rule as boqOutcome: ONE holder for the
  // transient per-attempt outcome, cleared before each new attempt so a
  // stale message can never sit beside a new one.
  const [docOutcome, setDocOutcome] = useState(null); // { type, message } | null
  const [selectedDocType, setSelectedDocType] = useState('specification');
  const boqInputRef = useRef(null);
  const docInputRef = useRef(null);

  // Handle BOQ file upload
  const handleBoqUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Clear any prior attempt's outcome BEFORE this attempt does anything
    // else, so no stale message can ever be visible alongside a new one.
    setBoqOutcome(null);

    if (!isValidBoqFile(file.name)) {
      // Same vocabulary as every other refusal on this screen.
      setBoqOutcome({
        type: 'error',
        message: `Could not attach ${file.name}. Accepted types: CSV, Excel (.xlsx, .xls).`,
      });
      if (boqInputRef.current) boqInputRef.current.value = '';
      return;
    }

    setUploadingBoq(true);
    try {
      // HELD MODE calls the stateless preview: same parse, same row ids,
      // same error shapes - which is what lets the catch below stay one
      // code path for both modes. NOTHING is written server-side; the File
      // is held for Continue to upload.
      const response = held
        ? await api.previewBoq(file)
        : await api.uploadBoq(procurementId, file);
      const count = response.data.boq_items?.length || 0;
      setBoqItems(response.data.boq_items || []);
      // boq_source is a persisted provenance record and the preview creates
      // none. Held mode synthesises the same shape from what it knows, so
      // the provenance line and the replacement caution read identically
      // before and after Continue - uploaded_at is the moment of SELECTION,
      // which is the honest answer while nothing has been uploaded.
      setBoqSource(held
        ? { filename: file.name, uploaded_at: new Date().toISOString(), item_count: count }
        : (response.data.boq_source || null));
      if (held && setHeldBoqFile) setHeldBoqFile(file);
      // A zero-item response can no longer reach this path (backend now
      // rejects zero-extraction as a 400) - this only ever fires with
      // count >= 1. Point-of-use, not a toast (house learning) - same
      // rule now applied uniformly to success as it already was to error.
      // ROW CAP WARNING AT POINT OF USE (item 10, 2026-09-03). The parser's
      // own cap travels on the response (row_cap, routes/documents.py, the
      // same constant the intake agent slices boq_items with); this reads
      // THAT and never a literal of its own, so the two cannot drift. Fires
      // only ABOVE the cap - a BOQ of exactly row_cap rows is read in full.
      // The upload is never refused: every row is stored; the buyer is told
      // which ones the analysis will not read.
      // WORDING (Ashok, 2026-09-03): only what the system supports. One BOQ
      // per procurement and a second upload REPLACES the first (upload-boq
      // does a $set of boq_items and logs boq_replaced; the client swaps
      // boqItems wholesale) - so "split the file" was wrong advice, and
      // trimming is the real option. A single excess row reads "Row 51",
      // not "Rows 51-51".
      const parseCap = Number.isFinite(response.data.row_cap) ? response.data.row_cap : null;
      const excess = parseCap !== null ? count - parseCap : 0;
      const rowsBeyondCap = excess > 0
        ? `This BOQ has ${count} rows; the analysis reads the first ${parseCap}. ${excess === 1 ? `Row ${count}` : `Rows ${parseCap + 1}-${count}`} will not be considered. If ${excess === 1 ? 'it matters' : 'they matter'}, trim the file to ${parseCap} rows and upload it again - the new file replaces this one.`
        : null;
      setBoqOutcome({ type: 'success', message: `${count} line item${count === 1 ? '' : 's'} imported from BOQ.`, rowsBeyondCap });
    } catch (error) {
      // Point-of-use, not a generic toast (house learning): the buyer
      // needs to see expected-vs-found columns right where the mismatch
      // happened, not in a transient banner elsewhere on the page.
      //
      // NAMED (Ashok's staging pass, 2026-08-21). The backend's detail says
      // "this file" and cannot say more - preview_boq is stateless and the
      // buyer may have tried several. The name is prefixed HERE, in the one
      // place that knows it, using the attach path's opening words
      // ("Could not attach {name}.") so a rejection at selection and a
      // failure at Continue read as one class of problem rather than two.
      // The expected-vs-found detail follows unchanged - it is the part
      // that tells the buyer what to fix.
      const detail = error.response?.data?.detail || 'Failed to upload BOQ file.';
      setBoqOutcome({ type: 'error', message: `Could not attach ${file.name}. ${detail}` });
      // RETAIN NO FILE ON ERROR (ruled). A rejected spreadsheet must not
      // sit invisibly in state waiting to be uploaded at Continue - the
      // buyer has been told it failed, and Continue must agree with that.
      // A previously ACCEPTED BOQ is a different matter and is untouched
      // here: upload-boq's own 400 already tells the buyer it remains in
      // effect, and this must not contradict it.
      if (held && setHeldBoqFile && !boqSource) setHeldBoqFile(null);
    } finally {
      setUploadingBoq(false);
      if (boqInputRef.current) boqInputRef.current.value = '';
    }
  };

  // Handle document upload
  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setDocOutcome(null);

    if (file.size > MAX_FILE_SIZE) {
      toastError(`File size must be less than ${MAX_FILE_SIZE_MB}MB`);
      return;
    }

    // TYPE CHECKED AT SELECTION, reported INLINE in this card the way the
    // BOQ card reports a preview rejection - point-of-use, where the
    // mismatch happened, naming the file. A refused file is NOT held.
    if (!isValidDocumentFile(file.name)) {
      setDocOutcome({
        type: 'error',
        message: `Could not attach ${file.name}. Accepted types: PDF, Word, Excel, CSV, TXT.`,
      });
      if (docInputRef.current) docInputRef.current.value = '';
      return;
    }

    // HELD MODE: no server call at all. A supporting document needs no
    // parse to be listed, so holding it is the whole operation - which is
    // why this returns before the try block rather than branching inside
    // it. The temp request id gives the row a stable local key; it is
    // never sent and never shown.
    if (held) {
      const localId = `${getTempRequestId ? getTempRequestId() : 'tmp'}-doc-${Date.now()}`;
      setHeldDocuments(prev => [...prev, {
        id: localId,
        file,
        filename: file.name,
        document_type: selectedDocType,
        size: file.size,
      }]);
      toastSuccess(`Document '${file.name}' added — it will be attached when you continue`);
      if (docInputRef.current) docInputRef.current.value = '';
      return;
    }

    setUploadingDoc(true);
    try {
      const response = await api.uploadDocument(procurementId, file, selectedDocType);
      // BUG FIX (2026-07-27): uploaded_at was never in the upload
      // response (backend fix: routes/documents.py's upload_document),
      // so a document attached THIS session always had it undefined
      // here - the RFP preview then showed "N/A" for a date the .docx
      // (reading the stored record directly) rendered correctly.
      const newDoc = {
        id: response.data.document_id,
        filename: response.data.filename,
        document_type: response.data.document_type,
        uploaded_at: response.data.uploaded_at,
        size: response.data.size
      };
      setAttachedDocuments(prev => [...prev, newDoc]);
      toastSuccess(`Document '${file.name}' uploaded successfully`);
    } catch (error) {
      // Inline, and NAMING THE FILE - same vocabulary as the selection-time
      // refusal above and as the Continue-time attach toast, so one class of
      // failure reads one way wherever the buyer meets it.
      setDocOutcome({
        type: 'error',
        message: `Could not attach ${file.name}. ${error.response?.data?.detail || 'Please try again.'}`,
      });
    } finally {
      setUploadingDoc(false);
      if (docInputRef.current) docInputRef.current.value = '';
    }
  };

  // Handle document delete
  // REMOVE A HELD BOQ. Client-only: drops the File, clears the preview
  // lines and the synthesised provenance, and makes NO server call - there
  // is nothing on the server to call about, and post-Continue removal is a
  // separate decision (it raises what happens to the line items derived
  // from the BOQ, and to the BOQ-presence quantity-question suppression).
  //
  // MARKS NOTHING DIRTY, deliberately. hasUnsavedChanges is
  // `dirtySteps.size > 0 || hasHeldFiles`, and markStepDirty('intake') has
  // exactly one caller - the requirement textarea's onChange. So dropping
  // the last held file with no typed text clears the warning on its own,
  // and this handler must not quietly add a reason to keep it.
  const handleRemoveHeldBoq = () => {
    if (setHeldBoqFile) setHeldBoqFile(null);
    setBoqItems([]);
    setBoqSource(null);
    setBoqOutcome(null);
    if (boqInputRef.current) boqInputRef.current.value = '';
    toastSuccess('BOQ removed');
  };

  // REMOVE A SERVER-SIDE BOQ (Ashok ruling 2026-09-03: DELETE REGENERATES,
  // exactly as an intake edit does). The first build of the post-Continue
  // removal - nothing like it existed on any branch
  // (reports/2026-09-04-post-continue-boq-removal.md, Finding 1). Replace
  // could never express "no BOQ".
  //
  // THE CASCADE IS INHERITED, NOT INVOKED. This clears the client's
  // boqItems/boqSource and nothing else, the way handleDeleteDoc below
  // clears attachedDocuments: the entry snapshot taken at "Edit Anyway"
  // no longer matches, so Continue re-parses and marks
  // STEP_DEPENDENTS.intake stale, and boq_items is an input to every
  // downstream hash on the server, so each step regenerates on its own
  // next entry. No line-item or question state is touched here.
  //
  // CONFIRMED FIRST, in the intake edit dialog's shape and sentence - the
  // consent is the same consent (StepIntake.jsx's "Edit the requirement?").
  // The server call is irreversible: the file itself was never stored,
  // only its parsed rows, so there is nothing to restore from.
  const [showRemoveBoqConfirm, setShowRemoveBoqConfirm] = useState(false);
  const [removingBoq, setRemovingBoq] = useState(false);
  const handleRemoveServerBoq = async () => {
    setRemovingBoq(true);
    try {
      await api.deleteBoq(procurementId);
      setBoqItems([]);
      setBoqSource(null);
      setBoqOutcome(null);
      if (boqInputRef.current) boqInputRef.current.value = '';
      setShowRemoveBoqConfirm(false);
      toastSuccess('BOQ removed');
    } catch (error) {
      toastError(error.response?.data?.detail || 'Failed to remove the BOQ');
    } finally {
      setRemovingBoq(false);
    }
  };

  const handleDeleteDoc = async (documentId, filename) => {
    // A HELD document was never uploaded, so removing it is local only -
    // calling deleteDocument would 404 against an id the server has never
    // seen.
    if (heldDocuments.some(d => d.id === documentId)) {
      setHeldDocuments(prev => prev.filter(d => d.id !== documentId));
      toastSuccess(`Document '${filename}' removed`);
      return;
    }
    try {
      await api.deleteDocument(procurementId, documentId);
      setAttachedDocuments(prev => prev.filter(d => d.id !== documentId));
      toastSuccess(`Document '${filename}' removed`);
    } catch {
      toastError('Failed to delete document');
    }
  };

  return (
    <div className="grid gap-5 md:grid-cols-2 items-start">
      {/* BOQ Upload Section - PROC-000063 fix: unconditional, same as the
          Additional Documents section below. Previously gated on
          procurementType (goods/mixed only), which could resolve
          differently across entry paths (fresh intake vs a resumed
          procurement - see StepIntake.jsx's branch-parity note) and made
          the box disappear entirely depending on how the user got here.
          ONE structure for every entry path; an empty state covers "no
          BOQ uploaded" instead of hiding the box. */}
      <div className="bg-white border border-zinc-200 p-5 rounded-sm">
        <div className="flex items-center gap-3 mb-3">
          <FileSpreadsheet className="w-5 h-5 text-[#0055FF]" />
          <h3 className="text-lg font-semibold text-zinc-900">Upload Bill of Quantities (BOQ)</h3>
        </div>

        {/* WHAT A GOOD FILE CONTAINS - not what the parser enforces
            (Ashok ruling, 2026-08-18). The parser stays TOLERANT and
            defaults what is missing; this states what a buyer should
            supply so that defaulting rarely has to happen. The previous
            wording ("Quantity and UoM are optional and default if
            missing") was false as written - the default fired only when
            the COLUMN was absent, so a blank CELL stored NaN or the
            literal string "nan" - and it invited exactly the empty-UoM
            file that then reached the agent. Keep in sync with
            BOQ_EXPECTED_COLUMNS_HELP in backend/routes/documents.py,
            which carries the same instruction. */}
        <p className="text-sm text-zinc-600 mb-3">
          Upload an Excel or CSV file with your procurement items. Each row should carry a <strong>Product Name</strong> and/or <strong>Description</strong> (at least one), a <strong>Quantity</strong>, and a <strong>UoM</strong> - the UoM should be a value from the UoM master (e.g. Each, Metre, Box). Rows with no product name and no description are skipped.
          {' '}
          {/* STANDING, not only on exceed: the cap is visible before a file
              is chosen. Rendered only once the value has arrived from the
              server - never a literal (mutant AV4). The replacement rule is
              always true and always shown. */}
          {rowCap !== null && (
            <span data-testid="boq-row-cap-standing">The analysis reads the first {rowCap} rows of a BOQ.</span>
          )}
          {' '}
          <span data-testid="boq-replace-rule">One BOQ per procurement - uploading another file replaces it.</span>
        </p>

        <div className="flex items-center gap-4">
          <input
            ref={boqInputRef}
            type="file"
            accept={VALID_BOQ_EXTENSIONS.join(',')}
            onChange={handleBoqUpload}
            className="hidden"
            id="boq-upload"
          />
          <Button
            variant="outline"
            onClick={() => boqInputRef.current?.click()}
            disabled={uploadingBoq}
            className="border-zinc-200"
          >
            {uploadingBoq ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            {uploadingBoq ? 'Uploading...' : 'Upload BOQ File'}
          </Button>
          <span className="text-xs text-zinc-500">Supported: CSV, Excel (.xlsx, .xls)</span>
        </div>

        {/* Persistent state (2026-07-30 restyle): describes the
            procurement, not an attempt - never cleared by upload
            lifecycle, only changes when boqSource changes. A permanent
            condition isn't a caution, so this is neutral helper text on
            the control (what it does), not a standalone amber block. */}
        {boqSource && (
          <p className="mt-2 text-xs text-zinc-500" data-testid="boq-replacement-caution">
            Uploading a new file replaces the current BOQ and rebuilds the line items.
          </p>
        )}
        {boqSource && (
          <p className="mt-1 text-xs text-zinc-500" data-testid="boq-provenance-line">
            Source: {boqSource.filename}
          </p>
        )}

        {/* REMOVE A HELD BOQ. Rendered whenever a File is HELD - a held File
            is by definition not on the server, so client-only removal is
            always the right action for it. This used to be gated on
            `held && heldBoqFile` (held = no procurementId), which hid the
            row and its control in the one state where the file is still
            held AFTER an id exists: a Continue whose upload-boq call failed
            (WizardContext keeps heldBoqFile and tells the buyer to "try
            these again" - with no control to try with). Found 2026-09-03;
            the K1-K3 suite never rendered that state. After a SUCCESSFUL
            Continue heldBoqFile is null and this renders nothing; removing
            a server-side BOQ is a separate, open ruling (non-goal). Same
            Trash2 idiom as the document rows. */}
        {heldBoqFile && (
          <div className="mt-3 flex items-center justify-between bg-zinc-50 border border-zinc-200 px-4 py-2 rounded-sm">
            <div className="flex items-center gap-3 min-w-0">
              <FileSpreadsheet className="w-4 h-4 text-zinc-500 shrink-0" />
              <span className="text-sm text-zinc-700 truncate">{heldBoqFile.name}</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRemoveHeldBoq}
              className="text-zinc-400 hover:text-red-500"
              aria-label={`Remove ${heldBoqFile.name}`}
              data-testid="remove-held-boq-btn"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        )}

        {/* REMOVE A SERVER-SIDE BOQ (2026-09-04, the ruling the block above
            named as open). Renders when the BOQ is ON THE SERVER: an id
            exists, no File is held (a held File has its own control above -
            the two states are exclusive by construction: heldBoqFile is
            cleared on a successful upload and kept on a failed one), and
            either boq_source or rows are present (a legacy document
            uploaded before boq_source existed has rows and no source). In
            held mode there is no id, so this never renders there. Same
            Trash2 idiom, same row shape. The click CONFIRMS, below. */}
        {Boolean(procurementId) && !heldBoqFile && (Boolean(boqSource) || (boqItems && boqItems.length > 0)) && (
          <div className="mt-3 flex items-center justify-between bg-zinc-50 border border-zinc-200 px-4 py-2 rounded-sm">
            <div className="flex items-center gap-3 min-w-0">
              <FileSpreadsheet className="w-4 h-4 text-zinc-500 shrink-0" />
              <span className="text-sm text-zinc-700 truncate">{boqSource?.filename || 'Bill of Quantities'}</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowRemoveBoqConfirm(true)}
              className="text-zinc-400 hover:text-red-500"
              aria-label={`Remove ${boqSource?.filename || 'Bill of Quantities'}`}
              data-testid="remove-server-boq-btn"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        )}

        {/* THE CONSENT IS THE SAME CONSENT. Same Dialog shape and the same
            second sentence as StepIntake.jsx's "Edit the requirement?" -
            what happens after a removal IS what happens after an intake
            edit (the hash regenerates, answers are re-paired by
            field_key), so the buyer is told the same thing in the same
            words. FileUploadSection.removeServerBoq.test.jsx asserts the
            sentence against StepIntake's source, so the two cannot drift
            apart silently. */}
        <Dialog open={showRemoveBoqConfirm} onOpenChange={setShowRemoveBoqConfirm}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Remove the BOQ?</DialogTitle>
              <DialogDescription>
                Removing the BOQ will re-analyze the requirement and regenerate
                downstream content. Answers you've already given are kept
                where questions still apply; anything tied to removed or
                changed questions will be reset.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRemoveBoqConfirm(false)} disabled={removingBoq}>
                Cancel
              </Button>
              <Button
                onClick={handleRemoveServerBoq}
                disabled={removingBoq}
                className="bg-[#0055FF] hover:bg-[#0044CC] text-white"
                data-testid="confirm-remove-boq-btn"
              >
                {removingBoq ? 'Removing...' : 'Remove Anyway'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Transient outcome (2026-07-30): exactly one of success/error,
            driven by the single boqOutcome state - point-of-use (house
            learning), never a toast. Cleared at the start of every new
            attempt, so a stale result can never coexist with a new one. */}
        {boqOutcome?.type === 'success' && (
          <p className="mt-2 text-xs text-green-700" data-testid="boq-upload-success">
            {boqOutcome.message}
          </p>
        )}
        {boqOutcome?.type === 'success' && boqOutcome.rowsBeyondCap && (
          <p className="mt-1 text-xs text-amber-700" data-testid="boq-row-cap-warning" role="status">
            {boqOutcome.rowsBeyondCap}
          </p>
        )}
        {boqOutcome?.type === 'error' && (
          <p className="mt-2 text-xs text-red-600" data-testid="boq-upload-error">
            {boqOutcome.message}
          </p>
        )}

        {/* BOQ Items Preview */}
        {boqItems && boqItems.length > 0 && (
          <div className="mt-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              {/* TWO FACTS, ONE LINE (Ashok's staging pass, 2026-09-04):
                  boqItems.length is the PARSED count - every row is stored -
                  and the analysis reads only the first rowCap of them. The
                  headline used to assert "N items loaded" beside a warning
                  and a standing sentence that both named the cap, so it
                  stated the count that is not used. Above the cap it now
                  carries the cap too, from the same server value the
                  standing text reads (never a literal); at or under the cap
                  the clause is omitted rather than stated redundantly, and
                  when the cap is unknown nothing is guessed - the count
                  alone renders. */}
              <span className="text-sm font-medium text-green-700" data-testid="boq-loaded-headline">
                {boqItems.length} items loaded from BOQ
                {rowCap !== null && boqItems.length > rowCap ? ` - the analysis reads the first ${rowCap}` : ''}
              </span>
            </div>
            <div className="bg-zinc-50 border border-zinc-200 rounded-sm overflow-hidden max-h-48 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-zinc-100 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-zinc-700">#</th>
                    <th className="px-3 py-2 text-left font-medium text-zinc-700">Product Name</th>
                    <th className="px-3 py-2 text-left font-medium text-zinc-700">Qty</th>
                    <th className="px-3 py-2 text-left font-medium text-zinc-700">UoM</th>
                  </tr>
                </thead>
                <tbody>
                  {boqItems.slice(0, BOQ_PREVIEW_LIMIT).map((item, idx) => (
                    <tr key={`boq-${item.line_number}-${item.product_name?.slice(0, 10) || idx}`} className="border-t border-zinc-100">
                      <td className="px-3 py-2 text-zinc-500">{item.line_number}</td>
                      <td className="px-3 py-2 text-zinc-700">{item.product_name}</td>
                      <td className="px-3 py-2 text-zinc-600">{item.quantity}</td>
                      <td className="px-3 py-2 text-zinc-600">{item.uom}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {boqItems.length > BOQ_PREVIEW_LIMIT && (
                <div className="px-3 py-2 text-xs text-zinc-500 bg-zinc-100">
                  ... and {boqItems.length - BOQ_PREVIEW_LIMIT} more items
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Additional Documents Section */}
      <div className="bg-white border border-zinc-200 p-5 rounded-sm">
        <div className="flex items-center gap-3 mb-3">
          <FileText className="w-5 h-5 text-[#0055FF]" />
          <h3 className="text-lg font-semibold text-zinc-900">Attach Additional Documents</h3>
        </div>

        <p className="text-sm text-zinc-600 mb-3">
          Upload specifications, designs, drawings, or other documents to be shared with vendors.
        </p>

        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Label htmlFor="doc-type" className="text-sm text-zinc-600">Document Type:</Label>
            <Select value={selectedDocType} onValueChange={setSelectedDocType}>
              <SelectTrigger className="w-48 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DOCUMENT_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <input
            ref={docInputRef}
            type="file"
            accept={VALID_DOCUMENT_EXTENSIONS.join(',')}
            onChange={handleDocUpload}
            className="hidden"
            id="doc-upload"
          />
          <Button
            variant="outline"
            onClick={() => docInputRef.current?.click()}
            disabled={uploadingDoc}
            className="border-zinc-200"
          >
            {uploadingDoc ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            {uploadingDoc ? 'Uploading...' : 'Upload Document'}
          </Button>
        </div>

        {/* TRANSIENT PER-ATTEMPT OUTCOME, inline and point-of-use - the
            same treatment the BOQ card gives a rejection, for the same
            reason: a type refusal is about the file the buyer just picked,
            so it belongs beside the control they picked it with, not in a
            banner elsewhere. Cleared at the start of every new attempt. */}
        {docOutcome?.type === 'error' && (
          <p className="mt-2 text-xs text-red-600" data-testid="doc-upload-error">
            {docOutcome.message}
          </p>
        )}

        {/* Copy addition (2026-07-27): persistent helper text, not a
            tooltip - replaces the old standalone "Max 10MB per file"
            label. Sets Beta-analysis-depth expectations up front so a
            large/lengthy upload's limited analysis isn't a surprise. */}
        <div className="mt-2 max-w-2xl space-y-1">
          <p className="text-xs font-medium text-zinc-600">Maximum file size: {MAX_FILE_SIZE_MB} MB per file.</p>
          <p className="text-xs text-zinc-500 leading-relaxed">
            Supporting documents help ProcMatrix understand your requirement. For best results, upload concise extracts or key sections containing the information most relevant to your request. During this Beta release, very large or lengthy documents may not be fully analyzed. If critical information is contained within a large document, include it in the requirement description or upload the relevant extract.
          </p>
        </div>

        {/* Attached Documents List - server-side and held, in ONE list.
            A buyer does not distinguish "attached" from "about to be
            attached"; both are files they have chosen and can remove, and
            two lists would ask them to care about a persistence detail
            they were never told about. handleDeleteDoc tells them apart by
            id, which is where the difference actually matters. */}
        {listedDocuments.length > 0 && (
          <div className="mt-4">
            <div className="text-sm font-medium text-zinc-700 mb-2">Attached Documents ({listedDocuments.length})</div>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {listedDocuments.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between bg-zinc-50 border border-zinc-200 px-4 py-3 rounded-sm">
                  <div className="flex items-center gap-3">
                    <FileText className="w-4 h-4 text-zinc-500" />
                    <div>
                      <div className="text-sm font-medium text-zinc-700">{doc.filename}</div>
                      <div className="text-xs text-zinc-500">
                        {DOCUMENT_TYPES.find(t => t.value === doc.document_type)?.label || doc.document_type}
                        {doc.size && ` • ${formatFileSize(doc.size)}`}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteDoc(doc.id, doc.filename)}
                    className="text-zinc-400 hover:text-red-500"
                    /* ACCESSIBLE NAME ONLY - no behaviour change. This
                       control predates the held-BOQ one beside it and was
                       an unlabelled icon button, so the two idioms were
                       identical to look at and not to a screen reader.
                       Naming it also gives the removal tests a way to say
                       WHICH row they clicked, which is what makes "remove
                       one of two" a real assertion. */
                    aria-label={`Remove ${doc.filename}`}
                    data-testid={`remove-doc-btn-${doc.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileUploadSection;
